/**
 * ═══════════════════════════════════════════════════════════════════════════
 * GLOBAL STATE STORE - Applicazione CRF
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * Gestisce lo stato globale dell'applicazione con:
 * - Dati header e footer comuni a tutte le pagine
 * - Contenuto dinamico delle singole pagine
 * - Stati di loading, errore e not found
 * 
 * STRUTTURA DATI:
 * Lo state contiene sezioni per header, footer, contenuto pagina,
 * e flags per gestire lo stato dell'applicazione.
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

// ───────────────────────────────────────────────────────────────────
// IMPORTS
// ───────────────────────────────────────────────────────────────────

import { reactive } from 'vue'
import { getPageData } from '@/api/api.js'

// ───────────────────────────────────────────────────────────────────
// STATE
// ───────────────────────────────────────────────────────────────────

export const state = reactive({
  /* ─────────────────────────────────────────────────────────────────
     LAYOUT GLOBALE
     ───────────────────────────────────────────────────────────────── */
  header: {
    logoMain: { image: '', link: '/' },
    menuItems: [],
    actionIcons: []
  },
  footer: { text: '' },

  /* ─────────────────────────────────────────────────────────────────
     CONTENUTO PAGINA
     ───────────────────────────────────────────────────────────────── */
  page: {},
  pageSections: [],

  /* ─────────────────────────────────────────────────────────────────
     FLAGS DI STATO
     ───────────────────────────────────────────────────────────────── */
  loading: false,
  error: null,
  notFound: false,

  /* ─────────────────────────────────────────────────────────────────
     HOVER STATE
     ───────────────────────────────────────────────────────────────── */
  hoverSection: 'left',
  // true solo mentre il mouse è effettivamente su una delle due sezioni
  // (hoverSection resta sull'ultimo lato; questo distingue il "nessun hover")
  hoverActive: false
})

// ───────────────────────────────────────────────────────────────────
// FUNZIONI STORE
// ───────────────────────────────────────────────────────────────────

export function setHoverSection(section) {
  state.hoverSection = section
}

// Ritardo prima di spegnere l'hover: fa da "ponte" tra il pannello e i link in
// alto. Uscendo dal pannello per salire sui link, l'enter dei link cancella il
// timer e l'hover non si chiude (niente sfarfallio/chiusura prematura).
let hoverHideTimer = null
export function setHoverActive(active) {
  if (active) {
    if (hoverHideTimer) { clearTimeout(hoverHideTimer); hoverHideTimer = null }
    state.hoverActive = true
  } else {
    if (hoverHideTimer) clearTimeout(hoverHideTimer)
    hoverHideTimer = setTimeout(() => {
      state.hoverActive = false
      hoverHideTimer = null
    }, 140)
  }
}

/**
 * Carica il contenuto della pagina dall'API
 * 
 * Recupera i dati dal backend per l'endpoint specificato e popola
 * lo stato reattivo globale. Gestisce gli stati di caricamento ed errore.
 * 
 * @param {string} apiUrl - L'endpoint API da chiamare (default '')
 * @returns {Promise<void>}
 */
export async function loadPageContent(apiUrl = '') {
  state.loading = true
  state.error = null
  state.notFound = false

  try {
    const data = await getPageData(apiUrl)

    if (!data) throw new Error('Risposta API vuota')

    // Reset stati prima di popolare
    state.error = null
    state.notFound = false

    // Gestione Global (Header/Footer)
    if (data.global) {
      if (data.global.header) {
        state.header = { ...state.header, ...data.global.header }
      }
      if (data.global.footer) {
        state.footer = { ...state.footer, ...data.global.footer }
      }
    }

    // Gestione Page e Sections
    if (data.page) {
      state.page = data.page
      state.pageSections = data.page.sections || []
    }

  } catch (err) {
    console.error('Errore nel caricamento dei dati store:', err)
    if (err.response?.status === 404) {
      state.notFound = true
    } else {
      state.error = err
    }
  } finally {
    state.loading = false
  }
}
